/**
 * Local help-assistant matcher — see bmc/docs/requirements/help-assistant-chat.md.
 *
 * Retrieval over a curated JSON knowledge base: no backend, no LLM. The
 * `answerQuestion` interface is intentionally narrow so a real LLM/RAG backend
 * can replace this implementation without touching the chat UI.
 */
import kbJson from "@/content/help/assistant-kb.json";

export interface KbLink {
  label: string;
  to: string;
}

export interface KbEntry {
  id: string;
  question: string;
  aliases: string[];
  answer: string;
  audience: "student" | "teacher" | "both";
  contexts: string[];
  links: KbLink[];
}

export interface AssistantContext {
  /** Current route pathname, used for context boosting. */
  route: string;
  /** Current role, used for audience filtering; null = anonymous (both-only). */
  role: "student" | "teacher" | null;
}

export interface AssistantReply {
  /** Matched entry, or null when nothing scored above the threshold. */
  entry: KbEntry | null;
  /** Next-best matches, offered as "related questions" chips. */
  related: KbEntry[];
}

const KB = kbJson as KbEntry[];

// Fail fast in dev on duplicate ids (AC 2).
if (import.meta.env.DEV) {
  const seen = new Set<string>();
  for (const e of KB) {
    console.assert(!seen.has(e.id), `[helpAssistant] duplicate KB id: ${e.id}`);
    seen.add(e.id);
  }
}

const STOP_WORDS = new Set([
  "a", "an", "the", "i", "my", "is", "are", "do", "does", "how", "what", "why",
  "can", "to", "of", "in", "on", "for", "it", "me", "and", "or", "be", "with",
]);

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter(t => t.length > 1 && !STOP_WORDS.has(t));
}

/** Light stemming: strip common English suffixes so "cancelled" ~ "cancel". */
function stem(token: string): string {
  const stemmed = token.replace(/(ing|ed|es|s)$/, "");
  // Degenerate stems ("ed" → "", "as" → "a") substring-match everything;
  // keep the original token when stemming leaves fewer than 2 characters.
  return stemmed.length >= 2 ? stemmed : token;
}

function scoreEntry(queryTokens: string[], entry: KbEntry, ctx: AssistantContext): number {
  const haystack = [entry.question, ...entry.aliases].map(t => tokenize(t).map(stem));
  const queryStems = queryTokens.map(stem);

  let best = 0;
  for (const candidate of haystack) {
    if (candidate.length === 0) continue;
    let hits = 0;
    for (const q of queryStems) {
      if (candidate.some(c => c === q || c.includes(q) || q.includes(c))) hits++;
    }
    // Normalise by query length so short queries can still reach the threshold.
    const coverage = hits / queryStems.length;
    const specificity = hits / candidate.length;
    best = Math.max(best, coverage * 0.7 + specificity * 0.3);
  }

  if (best === 0) return 0;

  // Context boost: entry tagged for the current route.
  if (entry.contexts.some(c => ctx.route.startsWith(c) && c !== "/")) best += 0.15;
  else if (entry.contexts.includes("/") && ctx.route === "/") best += 0.15;
  // Audience-specific entries edge out generic ones on ties.
  if (entry.audience !== "both") best += 0.02;

  return best;
}

const MATCH_THRESHOLD = 0.35;
const RELATED_COUNT = 3;

function audienceFilter(role: AssistantContext["role"]) {
  return (e: KbEntry) => (role ? e.audience === "both" || e.audience === role : e.audience === "both");
}

/** Answer a free-form question from the local knowledge base. */
export function answerQuestion(query: string, ctx: AssistantContext): AssistantReply {
  const queryTokens = tokenize(query);
  const eligible = KB.filter(audienceFilter(ctx.role));
  if (queryTokens.length === 0) return { entry: null, related: suggestedEntries(ctx) };

  const ranked = eligible
    .map(e => ({ e, score: scoreEntry(queryTokens, e, ctx) }))
    .filter(r => r.score > 0)
    .sort((a, b) => b.score - a.score);

  const top = ranked[0];
  if (!top || top.score < MATCH_THRESHOLD) {
    // Fallback: no confident answer — offer the closest questions instead.
    return { entry: null, related: ranked.slice(0, RELATED_COUNT).map(r => r.e) };
  }
  return { entry: top.e, related: ranked.slice(1, 1 + RELATED_COUNT).map(r => r.e) };
}

/** True when the entry is tagged for the given route. */
export function isRouteContextual(entry: KbEntry, route: string): boolean {
  return entry.contexts.some(c => c !== "/" && route.startsWith(c));
}

/** Role/route-aware suggested questions for the greeting (AC 9). */
export function suggestedEntries(ctx: AssistantContext, count = 4): KbEntry[] {
  const eligible = KB.filter(audienceFilter(ctx.role));
  const contextual = eligible.filter(e => isRouteContextual(e, ctx.route));
  const rest = eligible.filter(e => !contextual.includes(e));
  return [...contextual, ...rest].slice(0, count);
}

/** Every question the current role can ask, page-relevant ones first. */
export function allEntries(ctx: AssistantContext): KbEntry[] {
  return suggestedEntries(ctx, Number.POSITIVE_INFINITY);
}

/** Look up a KB entry by id (used when a suggestion chip is tapped). */
export function entryById(id: string): KbEntry | null {
  return KB.find(e => e.id === id) ?? null;
}
